import {formatError} from './format-error'
import {relayFailureDisposition, relayFailureFromError, type RelayFailure} from './relay-failure'
import {asAhpVersionMismatch} from './protocol-support'
import type {ProvisioningStatus} from './session-host/resolve-project'

// Retries for an `authenticate` the host could not complete (-32603). Four
// attempts with the 1 s, 2 s and 4 s backoff stay inside the 120 s connect wait
// even when each host identity check takes about 15 s to time out.
const AUTHENTICATE_UNAVAILABLE_RETRY_LIMIT = 3

interface RelayTerminalStateDeps {
  logger: {error(message: string): void}
  onProvisioning?: (status: ProvisioningStatus) => void
  teardownClient: () => Promise<void>
  setStatus: (status: 'error', error: string, failure: RelayFailure) => void
  setClientEverConnected: (clientEverConnected: false) => void
}

export class RelayTerminalState {
  private readonly deps: RelayTerminalStateDeps

  /**
   * Set once a connection attempt fails with a permanent, non-retryable
   * condition (a deliberate host `RpcError`, a protocol-version mismatch, or a
   * terminal provisioning failure). The runtime then holds a terminal `error`
   * status and stops the reconnect loop until it is disposed.
   */
  private terminated = false
  #authenticateUnavailableRetries = 0

  constructor(deps: RelayTerminalStateDeps) {
    this.deps = deps
  }

  isTerminated(): boolean {
    return this.terminated
  }

  /** Restore the bounded retry budget once a connection becomes healthy. */
  resetRetryBudget(): void {
    this.#authenticateUnavailableRetries = 0
  }

  #exhaustsRetryBudget(failure: RelayFailure): boolean {
    if (failure.kind !== 'rpc' || failure.subtype !== 'authenticateUnavailable') return false
    return this.#authenticateUnavailableRetries++ >= AUTHENTICATE_UNAVAILABLE_RETRY_LIMIT
  }

  /**
   * Handle a failed (re)establish. Returns `true` when the failure is terminal
   * — surfaced as an `error` status with no reconnect — so callers skip their
   * retry path. Terminal conditions are a protocol-version mismatch, a terminal
   * provisioning failure, any deliberate host `RpcError` outside the retryable
   * allowlist, or a bounded retryable failure that used up its retries.
   */
  async handleTerminalEstablishError(err: unknown): Promise<boolean> {
    const failure = relayFailureFromError(err)
    if (relayFailureDisposition(failure).automaticRetry !== 'stop' && !this.#exhaustsRetryBudget(failure)) return false
    const message =
      failure.kind === 'protocol-version' ? (asAhpVersionMismatch(err)?.message ?? formatError(err)) : formatError(err)
    this.terminated = true
    this.deps.setClientEverConnected(false)
    if (failure.kind === 'provisioning') {
      this.deps.logger.error(`relay project provisioning failed: ${message}`)
    } else {
      this.deps.logger.error(`relay connect failed (terminal): ${message}`)
    }
    // A partially-established connection may have already assigned an open
    // transport/client; release it before settling into the terminal `error`
    // state, since no later reconnect will run to clean it up.
    await this.deps.teardownClient()
    this.deps.setStatus('error', message, failure)
    if (failure.kind === 'provisioning') this.deps.onProvisioning?.({phase: 'failed', error: message})
    return true
  }
}
